import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock3, ShoppingBag, TriangleAlert, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { featureAllowed } from "@/lib/access";
import * as api from "@/lib/orderApi";
import ModuleUnavailable from "@/components/ModuleUnavailable";
import { MONEY_LINE, elapsedFromSeconds, inr } from "@/lib/format";
import { PageShell } from "@/components/PageShell";
import { EmptyState } from "@/components/ui/StateBoundaries";

const tabs = [
  ["live", "Running orders", ShoppingBag],
  ["channels", "Aggregator channels", UtensilsCrossed],
];

const COLUMNS = [
  ["open", "Punching"],
  ["kot_sent", "In the kitchen"],
  ["billing", "Bill printed"],
];

const elapsed = elapsedFromSeconds;

// A printed bill opens that bill, as on the tables board: without ?order= the till
// looked for a running order on the table, found none, and started a new one
// (owner, 2026-10-30).
const orderPath = order =>
  `/billing/table/${order.table_id}` + (order.status === "billing" ? `?order=${order.id}` : "");

export default function OrdersPage() {
  const navigate = useNavigate();
  const { token, subscribeRealtime, user, permissions } = usePOS();
  const canSeePrinted = featureAllowed("orders.pending_bills", user, permissions);
  const [tab, setTab] = useState("live");
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState(null);
  const [firingId, setFiringId] = useState(null);
  const [, setNow] = useState(new Date());

  const handleFireKot = async (order) => {
    setFiringId(order.id);
    try {
      const current = await api.getOrder(order.id, token);
      await api.fireKot(order.id, current.version, token);
      toast.success(`KOT sent to kitchen for ${order.table_number ? `Table ${order.table_number}` : `Order #${order.order_number}`}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Failed to fire KOT");
    } finally {
      setFiringId(null);
    }
  };

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      setOrders(await api.listOrders({}, token));
      setError(null);
    } catch (failure) {
      setError(failure?.response?.data?.detail || "Could not load the order board");
    }
  }, [token]);

  useEffect(() => {
    refresh();
    const timer = setInterval(() => setNow(new Date()), 60000); // tick for timers
    return () => clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    if (typeof subscribeRealtime !== "function") return undefined;
    return subscribeRealtime(event => {
      if (event.type === "order_update" || event.type === "resync") refresh();
    });
  }, [refresh, subscribeRealtime]);

  const columns = useMemo(
    () =>
      Object.fromEntries(
        COLUMNS.map(([status]) => [status, orders.filter(order => order.status === status)])
      ),
    [orders]
  );

  // Running tables total (owner, 2026-10-25): what is on the floor and not yet in
  // sales. Running = not billed, at the board's pre-tax item value; printed bills at
  // their bill total. Money is summed in paise so it never drifts.
  const totals = useMemo(() => {
    const paise = value => Math.round(Number(value || 0) * 100);
    // A complimentary order is not a sale: left out of both totals.
    const running = [...columns.open, ...columns.kot_sent].filter(order => !order.comp_type);
    return {
      runningCount: running.length,
      running: running.reduce((sum, order) => sum + paise(order.amount), 0) / 100,
      printedCount: columns.billing.filter(order => !order.comp_type).length,
      printed: columns.billing.filter(order => !order.comp_type).reduce((sum, order) => sum + paise(order.bill_total ?? order.amount), 0) / 100,
    };
  }, [columns]);

  return (
    /*
     * No hero. The AppShell topbar already reads "Running Orders/KOT" — the eyebrow that
     * used to sit here said ORDER COMMAND under it, then an h1 said Running Orders,
     * then a subtitle restated both. Roughly 130px of a 1080p board spent before the
     * first order (§2.2).
     */
    <PageShell testId="omnichannel-orders-page">
      <div className="flex gap-snug overflow-x-auto scrollbar-hide border-b shrink-0" data-testid="module-tabs">
        {tabs.map(([id, label, Icon]) => {
          const isActive = tab === id;
          return (
            <button
              key={id}
              // DESIGN.md §4: underline tabs, no fill. The active label used to be
              // text-primary-foreground on a 5% tint — white on near-white (V1).
              className={`-mb-px flex items-center gap-snug px-base py-snug text-body font-medium transition-colors border-b-2 ${
                isActive ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setTab(id)}
              data-testid={`orders-tab-${id}-button`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          )
        })}
      </div>

      {error && (
        <div className="bg-destructive/10 text-destructive border border-destructive/20 p-base rounded-lg flex items-start gap-snug shrink-0" role="alert" data-testid="orders-error">
          <TriangleAlert className="w-4 h-4 shrink-0 mt-hair" />
          <span className="text-body font-semibold">{error}</span>
        </div>
      )}

      {tab === "live" && (
        <div className="flex flex-wrap items-baseline gap-x-section gap-y-tight shrink-0" data-testid="running-totals">
          <span className="text-body">
            <span className="text-muted-foreground">Running tables </span>
            <b className={MONEY_LINE} data-testid="running-total">{inr(totals.running)}</b>
            <span className="text-meta text-muted-foreground"> · {totals.runningCount} {totals.runningCount === 1 ? "order" : "orders"} not billed · before tax</span>
          </span>
          {/* Without the pending-bills switch the server leaves printed bills out of
              this board, so a total here would be a false zero: say nothing instead. */}
          {canSeePrinted && <span className="text-body">
            <span className="text-muted-foreground">Bill printed, not settled </span>
            <b className={MONEY_LINE} data-testid="printed-total">{inr(totals.printed)}</b>
            <span className="text-meta text-muted-foreground"> · {totals.printedCount} {totals.printedCount === 1 ? "bill" : "bills"}</span>
          </span>}
        </div>
      )}

      {tab === "live" && (
        // Below lg the three columns stack and the BOARD scrolls: side by side they
        // needed ~950px, so a tablet hid "Bill printed" off-screen, and stacked
        // fixed-height columns clipped their last card on a phone.
        <div className="flex-1 min-h-0 min-w-0 w-full flex flex-col lg:flex-row gap-loose overflow-y-auto lg:overflow-y-visible scroll-contain-x" data-testid="running-order-board">
          {COLUMNS.map(([status, label]) => {
            const columnOrders = columns[status];

            // Map status to accent colour
            // info = live work, warning = waiting on money (DESIGN.md §2.4).
            const accentColor = status === "billing" ? "bg-warning" : "bg-info";

            return (
              <section key={status} className="shrink-0 lg:shrink lg:flex-1 lg:min-w-0 lg:min-h-0 flex flex-col bg-card/50 rounded-xl border">
                {/*
                  * One padding layer per nesting level was the rule this board broke:
                  * column header p-4, scroller p-4, then card p-4 — 48px of padding
                  * between the column edge and an order number. The scroller owns the
                  * inset now; the header and the rows sit inside it at row density.
                  */}
                <header className="px-base py-snug border-b flex justify-between items-center bg-card rounded-t-xl shrink-0">
                  <div className="flex items-center gap-snug">
                    <span className={`w-2 h-2 rounded-full ${accentColor}`}></span>
                    <h3 className="text-meta font-medium text-card-foreground">{label}</h3>
                  </div>
                  <span className="text-micro font-medium bg-muted text-muted-foreground px-snug py-hair rounded-md tabular-nums">{columnOrders.length}</span>
                </header>

                <div className="lg:flex-1 lg:overflow-y-auto p-snug space-y-snug">
                  {columnOrders.map(order => (
                    /*
                      * Two lines, not five. Identity and money on the first; the three
                      * facts that were chips — covers, items, elapsed — collapsed onto
                      * the second as one meta line. A chip is 26px of border and padding
                      * around eight characters.
                      */
                    // Every object opens (DESIGN.md §1.6, V2): the card goes to its
                    // table on the till. An order without a table has nowhere to go
                    // there, so it does not pretend to be clickable.
                    <article
                      key={order.id}
                      className={`bg-card border rounded-lg px-base py-snug transition-colors ${order.table_id ? "cursor-pointer hover:border-primary/40" : ""}`}
                      role={order.table_id ? "link" : undefined}
                      tabIndex={order.table_id ? 0 : undefined}
                      onClick={order.table_id ? () => navigate(orderPath(order)) : undefined}
                      // Only Enter on the card itself: Enter on its Fire KOT button bubbled
                      // here and navigated away while the KOT fired.
                      onKeyDown={order.table_id ? event => { if (event.key === "Enter" && event.target === event.currentTarget) navigate(orderPath(order)); } : undefined}
                      data-testid={`running-order-${order.id}`}
                    >
                      <div className="flex justify-between items-baseline gap-snug">
                        <strong className="text-lede font-semibold text-card-foreground tabular-nums truncate">#{order.order_number}</strong>
                        {/* A printed bill shows what was billed (tax included), not the running pre-tax value. */}
                        <b className={`${MONEY_LINE} shrink-0`} data-testid={`running-order-${order.id}-amount`}>{inr(order.bill_total ?? order.amount)}</b>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-snug gap-y-hair mt-hair text-meta text-muted-foreground">
                        <span className="truncate">
                          {order.table_number ? `Table ${order.table_number}` : order.order_type} · {order.captain_name}
                        </span>
                        <span aria-hidden="true">·</span>
                        <span className="tabular-nums">{order.item_count} {order.item_count === 1 ? "item" : "items"}</span>
                        <span aria-hidden="true">·</span>
                        <span className="tabular-nums">{order.guest_count} {order.guest_count === 1 ? "cover" : "covers"}</span>
                        <span aria-hidden="true">·</span>
                        <span className={`flex items-center gap-hair tabular-nums ${order.elapsed_seconds > 1800 ? 'text-danger font-medium' : ''}`}>
                          <Clock3 className="w-3 h-3" />
                          {elapsed(order.elapsed_seconds)}
                        </span>
                      </div>

                      {status === "open" && (
                        <div className="mt-snug pt-snug border-t flex justify-between items-center">
                          <span className="text-micro font-medium text-warning bg-warning-subtle px-2 py-0.5 rounded border border-warning/30">
                            Unsent to kitchen
                          </span>
                          {/* A commit, so the action green (DESIGN.md §2.4 rule 1); 36px to stay clear of the card's own tap area. */}
                          <button
                            type="button"
                            className="bg-action text-action-foreground hover:bg-action/90 h-9 px-3 rounded-md text-meta font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50"
                            onClick={async (e) => {
                              e.stopPropagation();
                              await handleFireKot(order);
                            }}
                            disabled={firingId === order.id}
                            data-testid={`fire-kot-order-${order.id}`}
                          >
                            <UtensilsCrossed className="w-3.5 h-3.5" />
                            <span>{firingId === order.id ? "Firing…" : "Fire KOT"}</span>
                          </button>
                        </div>
                      )}
                    </article>
                  ))}

                  {!columnOrders.length && (
                    <EmptyState density="inline" title="Nothing here" message={`No orders are currently ${label.toLowerCase()}.`} />
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {tab === "channels" && (
        <div className="flex-1">
          <ModuleUnavailable
            title="Aggregator channels are not part of this build"
            detail="Swiggy, Zomato and other online channels need integrations that do not exist yet. Token displays and channel menu sync depend on the same plumbing."
            testId="channels-unavailable"
          />
        </div>
      )}
    </PageShell>
  );
}
